import { useEffect, useState } from "react";
import axios from "axios";
import { Sparkles, MessageSquare, Loader2, Send, Coins, ThumbsUp } from "lucide-react";
import { getStoredEmail } from "../lib/useNewsezeUser";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function NewsezeAnalysis({ articleId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  useEffect(() => {
    let alive = true;
    let pollTimer = null;
    const load = async () => {
      try {
        const r = await axios.get(`${API}/articles/${articleId}/analysis`, { timeout: 60000 });
        if (alive) {
          setData(r.data);
          // If this was a fast preview, poll once after 12s for the full LLM analysis
          if (r.data?.preview && !pollTimer) {
            pollTimer = setTimeout(async () => {
              try {
                const r2 = await axios.get(`${API}/articles/${articleId}/analysis`, { timeout: 60000 });
                if (alive && !r2.data?.preview) setData(r2.data);
              } catch {}
            }, 12000);
          }
        }
      } catch (e) {
        if (alive) setErr(e?.response?.data?.detail || "Analysis temporarily unavailable.");
      } finally {
        if (alive) setLoading(false);
      }
    };
    load();
    return () => { alive = false; if (pollTimer) clearTimeout(pollTimer); };
  }, [articleId]);

  return (
    <section className="mt-8 rounded-2xl border border-[var(--line)] bg-white p-6" data-testid="newseze-analysis">
      <div className="flex items-center gap-2 mb-3">
        <span className="text-[10px] uppercase tracking-[0.18em] font-black text-[var(--blue)] bg-[var(--sky)] rounded-full px-2 py-0.5 flex items-center gap-1">
          <Sparkles size={11} /> Newseze Analysis
        </span>
        {data?.word_count && (
          <span className="text-[10px] uppercase tracking-[0.14em] font-bold text-[var(--muted)]" data-testid="analysis-meta">
            {data.word_count} words · original commentary
            {data.preview && " · full read loading…"}
          </span>
        )}
      </div>
      {loading ? (
        <div className="flex items-center gap-2 text-[var(--muted)] text-[13px]">
          <Loader2 size={14} className="animate-spin" /> Newseze is writing the analysis…
        </div>
      ) : err ? (
        <p className="text-[13px] text-rose-700 italic">{err}</p>
      ) : (
        <article className="prose max-w-none text-[15.5px] leading-[1.75] text-[var(--ink)] whitespace-pre-wrap" data-testid="newseze-analysis-body">
          {data?.analysis}
        </article>
      )}
    </section>
  );
}

export function CommentsSection({ articleId }) {
  const email = getStoredEmail();
  const [comments, setComments] = useState([]);
  const [busy, setBusy] = useState(true);
  const [text, setText] = useState("");
  const [name, setName] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState("");
  const [creditMsg, setCreditMsg] = useState("");

  const load = async () => {
    setBusy(true);
    try {
      const r = await axios.get(`${API}/articles/${articleId}/comments`, { timeout: 8000 });
      setComments(r.data.comments || []);
    } catch {}
    finally { setBusy(false); }
  };

  useEffect(() => { load(); }, [articleId]);

  const submit = async (e) => {
    e.preventDefault();
    if (!email) { setErr("Subscribe to Newseze first (free) to post a comment."); return; }
    if (text.trim().length < 2) { setErr("Comment is too short."); return; }
    setSubmitting(true); setErr(""); setCreditMsg("");
    try {
      const r = await axios.post(`${API}/articles/${articleId}/comments`, {
        article_id: articleId, email, body: text.trim(), display_name: name.trim() || undefined,
      });
      setComments([r.data, ...comments]);
      setText("");
      if (r.data.credits_awarded > 0) setCreditMsg(`+${r.data.credits_awarded} credits for joining the conversation`);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not post comment.");
    } finally { setSubmitting(false); }
  };

  return (
    <section className="mt-8 rounded-2xl border border-[var(--line)] bg-white p-6" data-testid="comments-section">
      <div className="flex items-center gap-2 mb-4">
        <span className="text-[10px] uppercase tracking-[0.18em] font-black text-[var(--blue)] bg-[var(--sky)] rounded-full px-2 py-0.5 flex items-center gap-1">
          <MessageSquare size={11} /> Reader Comments
        </span>
        <span className="text-[10px] uppercase tracking-[0.14em] font-bold text-[var(--muted)]" data-testid="comments-summary">
          {comments.length === 0 ? "Be the first reader" : `${comments.length} reader${comments.length === 1 ? "" : "s"} weighed in`}
          {" · "}
          {!email ? "Subscribe to join" : "Earn +15 credits per post"}
        </span>
      </div>

      <form onSubmit={submit} className="mb-5" data-testid="comment-form">
        <div className="grid grid-cols-1 sm:grid-cols-[200px_1fr] gap-2 mb-2">
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Display name (optional)"
                 className="border border-[var(--line)] rounded-md p-2 text-[13px]" data-testid="comment-name-input" maxLength={60} />
        </div>
        <textarea value={text} onChange={(e) => setText(e.target.value)}
                  placeholder={email ? "Share your read on this story — keep it respectful." : "Subscribe free to join the conversation."}
                  className="w-full border border-[var(--line)] rounded-md p-2 text-[13.5px]" rows={3} maxLength={600}
                  data-testid="comment-text-input" />
        <div className="flex items-center justify-between mt-2 gap-2 flex-wrap">
          {err && <span className="text-[12px] text-rose-700 italic" data-testid="comment-error">{err}</span>}
          {creditMsg && <span className="text-[12px] text-[var(--gold)] font-black flex items-center gap-1" data-testid="comment-credit-msg"><Coins size={12} /> {creditMsg}</span>}
          <button type="submit" disabled={submitting || !text.trim()} className="btn primary ml-auto disabled:opacity-50" data-testid="comment-submit-btn">
            {submitting ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />}
            Post
          </button>
        </div>
      </form>

      {busy ? (
        <div className="text-[var(--muted)] text-[13px] flex items-center gap-2"><Loader2 size={13} className="animate-spin" /> Loading comments…</div>
      ) : comments.length === 0 ? (
        <p className="text-[13px] text-[var(--muted)] italic">No comments yet. Be the first.</p>
      ) : (
        <ul className="space-y-3">
          {comments.map((c) => (
            <li key={c.id} className="border-b border-[var(--line)] pb-3 last:border-0" data-testid={`comment-${c.id}`}>
              <div className="flex items-center gap-2 text-[12px] mb-1">
                <span className="font-black text-[var(--ink)]">{c.author || "Reader"}</span>
                <span className="text-[var(--muted)]">·</span>
                <span className="text-[var(--muted)]">{new Date(c.created_at).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}</span>
                <ThumbsUp size={11} className="text-[var(--muted)] ml-auto" />
                <span className="text-[11px] text-[var(--muted)] font-bold">{c.upvotes || 0}</span>
              </div>
              <p className="text-[13.5px] text-[var(--ink)] leading-snug">{c.body}</p>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
